import { defineStore } from 'pinia';
import { DocumentAction, DocumentListItem, DocumentListItemResponse } from '@/models/documents/document-list.model';
import { DEFAULT_LIMIT, DEFAULT_MAX_LIMIT, DEFAULT_OFFSET, DEFAULT_PAGE_NUMBER } from '@/constants/common/constants';
import { ResponseWithHeaders } from '@/models/common/response-with-header.model';
import { api } from '@/services/http.service';
import { ResponseHeaders } from '@/models/common/headers.model';
import { DocumentListFilter } from '@/models/documents/document-list-filter.model';

interface DocumentListState {
  documentList: DocumentListItem[];
  isDocumentListLoading: boolean;
  limit: number;
  currentPage: number;
}

export const useDocumentListStore = defineStore('DocumentListStore', {
  state: (): DocumentListState => ({
    documentList: [],
    isDocumentListLoading: false,
    limit: DEFAULT_LIMIT,
    currentPage: DEFAULT_PAGE_NUMBER,
  }),
  getters: {
    documentListCount(state: DocumentListState): number {
      return state.documentList.length;
    },
    paginatedDocumentList(state: DocumentListState): DocumentListItem[] {
      const offset = (state.currentPage - 1) * state.limit;

      return state.documentList.slice(offset, offset + state.limit);
    },
    // TODO: Change the current realisation than the backend is enabled to support multibrand functionality
    adminTakenPlayerCount(state: DocumentListState): number {
      return state.documentList.filter(
        (documentListItem: DocumentListItem) => documentListItem.documentModerationAction === DocumentAction.DROP,
      ).length;
    },
  },
  actions: {
    setDocumentListLoading(isLoading: boolean): void {
      this.isDocumentListLoading = isLoading;
    },
    setDocumentList(documentList: DocumentListItem[]): void {
      this.documentList = documentList;
    },
    async getDocumentListByWhitelabel(
      whitelabelSlug: string,
      filterList: DocumentListFilter = {},
    ): Promise<DocumentListItemResponse[]> {
      const result: DocumentListItemResponse[] = [];

      let nextPageToken: string | null = null;

      do {
        const requestParams = {
          filterList,
          limit: DEFAULT_MAX_LIMIT,
          offset: DEFAULT_OFFSET,
          ...(nextPageToken && { nextPageToken }),
        };

        const {
          data,
          headers,
        }: ResponseWithHeaders<DocumentListItemResponse[]> =
          await api.documents.getDocumentListRequest.perform(
            whitelabelSlug,
            requestParams,
          );

        result.push(...data);

        const newNextPageToken =
          headers[ResponseHeaders.NEXT_PAGE_TOKEN_HEADER] ?? null;

        if (!newNextPageToken || newNextPageToken === nextPageToken) {
          break;
        }

        nextPageToken = newNextPageToken;
      } while (nextPageToken);

      return result;
    },
  },
});
